/**
 * What each role can reach — one table instead of conditionals scattered
 * through the Sidebar.
 *
 * The Sidebar used to branch on a binary `isAssistant`, which meant any role
 * that wasn't 'assistant' fell through every check and saw the entire app. A
 * new role should be a row here, not a new `&&` in six places.
 *
 * This governs NAVIGATION ONLY. It is a convenience, never a boundary — the
 * producer's territory lock is enforced server-side in
 * server/middleware/producerScope.js, because anything the browser decides can
 * be undecided by the browser.
 *
 * Both surfaces that offer navigation read this table: the Sidebar sections in
 * App.js and the page list in components/CommandPalette.js. Keep them in sync —
 * a page the sidebar hides but the palette offers is the bug this table exists
 * to prevent. Deliberate consequence: a signed-in assistant who types
 * /bid-results into the URL bar still gets the page. That is accepted. The
 * data is not secret from staff; the nav is curated so the outreach roles see
 * their own job first. Producers are the one role where this IS a boundary,
 * and that boundary lives on the server, not here.
 */
// 'reports' is gone: Reports is a tab inside the Command Center, so it is
// reached through 'dashboard' and has no nav key of its own.
// 'today' is gone the same way 'reports' was: Today is the DEFAULT tab of the
// Command Center, so it is reached through 'dashboard' and has no nav key of
// its own. /today still resolves — App.js redirects it.
const FULL = [
  'south-florida', 'workspace', 'dashboard', 'pipeline',
  'contractors', 'map', 'bid-intel', 'tools', 'admin',
];

const ACCESS = {
  admin: { home: '/dashboard', nav: FULL },
  user: { home: '/dashboard', nav: FULL },
  assistant: { home: '/workspace', nav: ['workspace', 'contractors', 'map'] },
  producer: { home: '/south-florida', nav: ['south-florida', 'map'] },
};

const forUser = (user) => ACCESS[user && user.role] || ACCESS.user;

/** Landing page for this role — where "/" and "/home" send them. */
export function homeFor(user) { return forUser(user).home; }

/** Set of nav keys this role may see. */
export function navFor(user) { return new Set(forUser(user).nav); }

export function isProducer(user) { return !!user && user.role === 'producer'; }

export default { homeFor, navFor, isProducer };
